import { Currency, CurrencyAmount } from '@cowprotocol/currency'
import { Command } from '@cowprotocol/types'
import { CowSwapWidgetAppParams } from '@cowprotocol/widget-lib'

import { PriceImpact } from 'legacy/hooks/usePriceImpact'

import { ApprovalState, ApproveRequiredReason } from 'modules/erc20Approve'
import { TradeDerivedState } from 'modules/trade'
import { TradeQuoteState } from 'modules/tradeQuote'

export interface TradeFormButtonContext {
  defaultText: string
  amountToApprove: CurrencyAmount<Currency> | null
  derivedState: TradeDerivedState
  recipientEnsAddress: string | null
  quote: TradeQuoteState
  isSupportedWallet: boolean
  widgetStandaloneMode?: boolean
  supportsPartialApprove?: boolean
  customTokenError?: string
  minAmountToSignForSwap?: CurrencyAmount<Currency>
  balancesError: string | null
  confirmClickEvent?: string
  approveClickEvent?: string
  widgetPriceImpactThreshold: number | undefined
  solanaNativeShortfall: CurrencyAmount<Currency> | null

  confirmTrade(): void

  connectWallet: Command

  wrapNativeFlow(): Promise<unknown>
}

export interface TradeFormValidationCommonContext {
  account: string | undefined
  derivedTradeState: TradeDerivedState
  approvalState: ApprovalState
  tradeQuote: TradeQuoteState
  recipientEnsAddress: string | null
  isWrapUnwrap: boolean
  isBundlingSupported: boolean | null
  isSupportedWallet: boolean
  isSwapUnsupported: boolean
  isSafeReadonlyUser: boolean
  isApproveRequired: ApproveRequiredReason
  isInsufficientBalanceOrderAllowed: boolean
  isProviderNetworkUnsupported: boolean
  isProviderNetworkDeprecated: boolean
  isOnline: boolean
  intermediateTokenToBeImported: boolean
  isAccountProxyLoading: boolean
  isProxySetupValid: boolean | null | undefined
  customTokenError?: string
  isRestrictedForCountry: boolean
  isBalancesLoading: boolean
  balancesError: string | null
  isInputCurrencyXstock: boolean
  isOutputCurrencyXstock: boolean
  injectedWidgetParams: Partial<CowSwapWidgetAppParams>
  tradePriceImpact: PriceImpact
  isNonEvmReceiverConfirmed: boolean
  isRestoringConnection: boolean
  isCaptchaPending: boolean
  isCaptchaRequired: boolean
  /**
   * For Swap only: the maximum sell amount the order can pull from the wallet, including slippage.
   * Used instead of the raw input amount when checking balance sufficiency, since a swap order
   * (unlike limit/TWAP orders) must never be placed if the wallet can't cover slippage in the worst case.
   * Null for non-swap trade types, where the raw input amount is used instead.
   */
  swapMaximumSellAmount: CurrencyAmount<Currency> | null
  /**
   * Solana only: native SOL the wallet is missing to cover account rent and the fee the trade
   * transaction pays on top of the sell amount. Null when it can afford them or on other chains.
   */
  solanaNativeShortfall: CurrencyAmount<Currency> | null
}

export interface TradeFormValidationContext extends TradeFormValidationCommonContext {}

export enum TradeFormValidation {
  // Wrap/unwrap
  WrapUnwrapFlow,

  // Quote errors
  QuoteErrors,
  CurrencyNotSupported,

  // Wallet
  WalletNotConnected,
  WalletNotSupported,
  SafeReadonlyUser,
  WalletCapabilitiesLoading,
  RestoringWallet,

  // Quote request params
  CurrencyNotSet,
  InputAmountNotSet,
  RecipientInvalid,
  RecipientNotSet,
  RecipientNotConfirmed,
  NetworkNotSupported,
  NetworkDeprecated,
  BrowserOffline,

  // Quote loading indicator
  CaptchaPending,
  CaptchaRequired,
  QuoteLoading,
  ImpactLoading,
  QuoteExpired,

  // Balances
  BalancesLoading,
  BalancesNotLoaded,
  BalanceInsufficient,
  SolanaInsufficientNativeBalance,

  // Approve
  ApproveAndSwapInBundle,
  ApproveRequired,

  // Intermediate token
  ImportingIntermediateToken,

  // Native. Pushed last by `validateTradeForm`, and `useGetTradeFormValidation` reads `validations[0]`,
  // so this is the last-resort reason: anything pushed earlier wins over it.
  SellNativeToken,

  // Bridging
  ProxyAccountLoading,
  ProxyAccountUnknown,
  CustomTokenError,

  // RWA/Geo restrictions
  RestrictedForCountry,
  XstockMinimumTradeSize,

  // Solana Alpha (unaudited launch)
  SolanaAlphaMaxTradeSize,
  SolanaAlphaUnknownUsdValue,

  // Widget controlled
  DisableTradeWithUnknownPriceImpact,
  DisableTradeWithHighPriceImpact,
  WidgetConstrainedTokenPair,
}
